import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth, useUser } from "@clerk/clerk-react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import "./Sidebar.css";
import { BRAND_LOGO, BRAND_NAME, SOCIAL_LINKS } from "../../config/app/brandAssets";
import {
  createIcon,
  batchIcon,
  aiToolsIcon,
  templatesIcon,
  mydesignsIcon,
  brandIcon,
  uploadsNavIcon as uploadsIcon,
  activityIcon,
  userProfileIcon,
  settingsNavIcon as settingsIcon,
  twitterIcon,
  youtubeIcon,
  instaIcon,
} from "../../icons/assets";
import SettingsModal from "../popups/SettingsModal";
import Pricing from "../popups/pricing/Pricing";
import logger from "../../utils/logger";
import { getBalance } from "../../services/api/app/credits";
import { syncUser } from "../../services/api/app/auth";
import {
  fetchAccountBootstrap,
  invalidateAccountBootstrap,
} from "../../services/api/app/accountBootstrap";
import AccountSettingsMenu from "../menus/AccountSettingsMenu";
import { IS_APP_V1, SHOW_BUY_MORE_CREDITS, SHOW_TOOLS_NAV } from "../../config/app/appVersion";
import { CREDITS_BALANCE_REFRESH_EVENT } from "../../config/app/signupCredits";
import { getUserDisplayName } from "../../utils/displayName";

const BYTES_PER_GIB = 1073741824;

const bytesToGb = (bytes) => {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.round((n / BYTES_PER_GIB) * 100) / 100;
};

const parseStorageQuotaGb = (benefits) => {
  const allocated = bytesToGb(benefits?.allocated_storage_bytes);
  if (allocated > 0) return allocated;
  const list = Array.isArray(benefits?.benefits) ? benefits.benefits : [];
  for (const item of list) {
    const match = String(item).match(/(\d+(?:\.\d+)?)\s*GB\s*storage/i);
    if (match) return Number(match[1]);
  }
  if (benefits?.has_subscription) return 5;
  return 50;
};

const storageUsedFromBenefits = (benefits, _quotaGb) => {
  const allocated = Number(benefits?.allocated_storage_bytes);
  const remaining = Number(benefits?.remaining_storage_bytes);
  if (Number.isFinite(allocated) && Number.isFinite(remaining) && allocated >= 0) {
    const usedBytes = Math.max(0, allocated - remaining);
    return Math.round((usedBytes / BYTES_PER_GIB) * 1000) / 1000;
  }
  return 0;
};

const formatStorageLabel = (usedGb, quotaGb) => {
  const used = Number(usedGb) || 0;
  const quota = Number(quotaGb) || 0;
  if (used > 0 && used < 0.1) {
    const usedMb = Math.max(1, Math.round(used * 1024));
    return `${usedMb} MB / ${quota} GB`;
  }
  const usedLabel = used >= 0.1 ? String(Math.round(used * 100) / 100) : "0";
  return `${usedLabel} GB / ${quota} GB`;
};

/** Escalate Buy More when balance is low; balance / storage / invite stay visible. */
const CREDITS_LOW_ABS = 50;
const CREDITS_CRITICAL_ABS = 10;
const CREDITS_LOW_RATIO = 0.2;
const CREDITS_CRITICAL_RATIO = 0.05;
const DEFAULT_BENEFITS = {
  has_subscription: false,
  plan_name: "Free",
  access_phase: "free",
  credit_display_total: 0,
};

const SidebarSkeleton = ({ className = "" }) => (
  <span className={`sidebar-skel ${className}`.trim()} aria-hidden="true" />
);

const Sidebar = (props) => {
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [settingsActiveItem, setSettingsActiveItem] = useState("profile");
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [showAccountMenu, setShowAccountMenu] = useState(false);
  const [creditsBalance, setCreditsBalance] = useState(0);
  const [monthlyCredits, setMonthlyCredits] = useState(0);
  const [storageQuotaGb, setStorageQuotaGb] = useState(50);
  const [storageUsedGb, setStorageUsedGb] = useState(0);
  const [subscriptionBenefits, setSubscriptionBenefits] = useState(DEFAULT_BENEFITS);
  const [accountDataReady, setAccountDataReady] = useState(false);
  const { signOut, getToken } = useAuth();
  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;
  const accountDataReadyRef = useRef(false);
  const { isLoaded: isUserLoaded, user } = useUser();
  const userId = user?.id;
  accountDataReadyRef.current = accountDataReady;
  const navigate = useNavigate();
  const location = useLocation();
  const inviteActive = location.pathname === "/invite";
  const profileReady = isUserLoaded && !!user;

  const applyStorageFromBenefits = (benefits) => {
    const quota = parseStorageQuotaGb(benefits);
    setStorageQuotaGb(quota);
    setStorageUsedGb(storageUsedFromBenefits(benefits, quota));
  };

  const parseCreditsValue = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  };

  const loadSidebarAccountData = useCallback(
    async (token, { force = false } = {}) => {
      if (IS_APP_V1) {
        try {
          const response = await getBalance(token);
          const credits = parseCreditsValue(response?.credits);
          if (response?.success && credits != null) {
            setCreditsBalance(credits);
            setMonthlyCredits(0);
            applyStorageFromBenefits(null);
            setSubscriptionBenefits(DEFAULT_BENEFITS);
            setAccountDataReady(true);
            return true;
          }
        } catch (err) {
          logger.warn("Failed to fetch credits balance", err);
        }
        return false;
      }

      try {
        if (force) invalidateAccountBootstrap();
        const bootstrap = await fetchAccountBootstrap(token, { force });
        if (!bootstrap?.success) return false;

        const credits = parseCreditsValue(bootstrap.credits);
        if (credits != null) {
          setCreditsBalance(credits);
        }
        const monthly = parseCreditsValue(bootstrap.monthly_credits);
        if (monthly != null) {
          setMonthlyCredits(monthly);
        }
        if (bootstrap.benefits) {
          applyStorageFromBenefits(bootstrap.benefits);
          setSubscriptionBenefits({
            ...DEFAULT_BENEFITS,
            ...bootstrap.benefits,
          });
        }
        if (credits != null) {
          setAccountDataReady(true);
          return true;
        }
        return false;
      } catch (err) {
        logger.warn("Failed to load account bootstrap", err);
        return false;
      }
    },
    []
  );

  useEffect(() => {
    let cancelled = false;
    let retryTimer = null;
    const MAX_ATTEMPTS = 10;

    const fetchCreditsBalance = async (attempt = 0) => {
      try {
        const token = await getTokenRef.current();
        if (!token) {
          if (!cancelled && attempt < MAX_ATTEMPTS) {
            retryTimer = window.setTimeout(() => {
              fetchCreditsBalance(attempt + 1);
            }, 700);
          } else if (!cancelled) {
            setAccountDataReady(true);
          }
          return;
        }

        // Sync first so require_user_id has a DB user (avoids 403 → stuck at 0 credits).
        // If sync fails, still try balance (user may already exist) and retry.
        let synced = false;
        try {
          await syncUser(token);
          synced = true;
        } catch (syncErr) {
          logger.warn("Sync before balance fetch failed", syncErr);
        }

        const ok = await loadSidebarAccountData(token);
        if (ok || cancelled) return;

        if (attempt < MAX_ATTEMPTS) {
          const delay = synced ? 900 : 1400;
          retryTimer = window.setTimeout(() => {
            fetchCreditsBalance(attempt + 1);
          }, delay);
        } else {
          setAccountDataReady(true);
        }
      } catch (error) {
        logger.error("Failed to fetch credits balance", error);
        if (!cancelled && attempt < MAX_ATTEMPTS) {
          retryTimer = window.setTimeout(() => {
            fetchCreditsBalance(attempt + 1);
          }, 1200);
        } else if (!cancelled) {
          setAccountDataReady(true);
        }
      }
    };

    if (userId) {
      if (!accountDataReadyRef.current) setAccountDataReady(false);
      fetchCreditsBalance();
    }
    return () => {
      cancelled = true;
      if (retryTimer) window.clearTimeout(retryTimer);
    };
  }, [userId, loadSidebarAccountData]);

  useEffect(() => {
    const onCreditsRefresh = (event) => {
      const balance = event?.detail?.balance;
      if (typeof balance === "number") {
        setCreditsBalance(balance);
      }
      // Always re-bootstrap so storage/exports stay in sync after AI runs
      // (balance-only events used to skip this and leave Storage at 0/50).
      (async () => {
        try {
          const token = await getTokenRef.current();
          if (!token) return;
          await loadSidebarAccountData(token, { force: true });
        } catch (error) {
          logger.warn("Failed to refresh account after credits event", error);
        }
      })();
    };

    window.addEventListener(CREDITS_BALANCE_REFRESH_EVENT, onCreditsRefresh);
    return () => window.removeEventListener(CREDITS_BALANCE_REFRESH_EVENT, onCreditsRefresh);
  }, [loadSidebarAccountData]);

  const refreshCredits = async () => {
    try {
      const token = await getToken();
      if (!token) return;
      await loadSidebarAccountData(token, { force: true });
    } catch (error) {
      logger.error("Failed to refresh credits balance", error);
    }
  };

  const openProfileUsage = () => {
    setShowAccountMenu(false);
    setSettingsActiveItem("profile");
    setShowSettingsModal(true);
  };

  const balance = Number(creditsBalance) || 0;
  const allotment = Number(monthlyCredits) || 0;
  const displayTotal = Math.max(
    Number(subscriptionBenefits?.credit_display_total) || 0,
    Number(subscriptionBenefits?.monthly_credits) || 0,
    balance,
    0
  );
  const creditCap = Math.max(displayTotal, 1);
  const creditsRatio = allotment > 0 ? balance / allotment : null;
  const creditsLow =
    accountDataReady &&
    (balance <= CREDITS_LOW_ABS ||
      (creditsRatio != null && creditsRatio <= CREDITS_LOW_RATIO));
  const creditsCritical =
    accountDataReady &&
    (balance <= CREDITS_CRITICAL_ABS ||
      (creditsRatio != null && creditsRatio <= CREDITS_CRITICAL_RATIO));

  const showBuyCta = (SHOW_BUY_MORE_CREDITS || !IS_APP_V1) && creditsLow;
  const showInviteCta = !IS_APP_V1;

  const creditsProgress = Math.max(
    0,
    Math.min(((displayTotal > 0 ? balance : 0) / creditCap) * 100, 100)
  );
  const storageProgress = Math.max(
    0,
    Math.min(
      storageQuotaGb > 0 ? (storageUsedGb / storageQuotaGb) * 100 : 0,
      100
    )
  );
  const planLabel =
    subscriptionBenefits?.plan_name ||
    (balance > 0 ? "Pay as you go" : "Free");
  const creditsLabel = "Credit";
  const displayName = profileReady ? getUserDisplayName(user) : "";

  const handleLogout = async () => {
    try {
      // Keep user on our /login (styled Landing + logos/), not Clerk Account Portal / Dashboard logo
      await signOut({ redirectUrl: "/login" });
      logger.info("User signed out successfully");
    } catch (error) {
      logger.error("Error during sign out", error);
      navigate("/login", { replace: true });
    }
  };
  return (
   <div className="sidebar-container">
      <div className="logo-section">
        <img
          id="app-logo"
          className="app-logo"
          src={BRAND_LOGO.sidebar}
          alt={BRAND_NAME}
        />
      </div>
      <p className="sidebar-headings">TOOLS</p>
      <div className="nav-item">
        <NavLink
          to="/dashboard"
          className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
          style={{
            justifyContent: "flex-start",
          }}
        >
          <img id="logo" src={createIcon} alt="" />
          <span>Create</span>
        </NavLink>
      </div>
      <div className="nav-item">
        <NavLink
          to="/batch"
          className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
          style={{
            justifyContent: "flex-start",
          }}
        >
          <img id="logo" src={batchIcon} alt="" />
          <span>Batch</span>
        </NavLink>
      </div>
      {SHOW_TOOLS_NAV ? (
        <div className="nav-item">
          <NavLink
            to="/tools"
            className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
            style={{
              justifyContent: "flex-start",
            }}
          >
            <img id="logo" src={aiToolsIcon} alt="" />
            <span>Tools</span>
          </NavLink>
        </div>
      ) : null}
      <div className="nav-item">
        <button
          type="button"
          className="nav-link nav-link-placeholder"
          style={{
            justifyContent: "flex-start",
          }}
        >
          <img id="logo" src={templatesIcon} alt="" />
          <span>Templates</span>
          <span className="coming-soon-badge">Coming soon</span>
        </button>
      </div>
     {/*  <div className="nav-item">
        <NavLink
          to="/water-mark-remover"
          className="nav-link"
          style={{
            justifyContent: "flex-start",
          }}
        >
          <img id="logo" src={videoBgRemoverIcon} alt="" />
          <span>Batch</span>
        </NavLink>
      </div>
      <div className="nav-item">
        <NavLink
          to="/video-bg-remover"
          className="nav-link"
          style={{
            justifyContent: "flex-start",
          }}
        >
          <img id="logo" src={videoBgRemoverIcon} alt="" />
          <span>Video BG Remover</span>
        </NavLink>
      </div> */}

      <hr className="divider" />
      <p className="sidebar-headings">MANAGE</p>
      <div className="nav-item">
        <NavLink
          to="/my-designs"
          className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
          style={{
            justifyContent: "flex-start",
          }}
        >
          <img id="logo" src={mydesignsIcon} alt="" />
          <span>My Designs</span>
        </NavLink>
      </div>
      <div className="nav-item">
        <button
          type="button"
          className="nav-link nav-link-placeholder"
          style={{
            justifyContent: "flex-start",
          }}
        >
          <img id="logo" src={brandIcon} alt="" />
          <span>Brand Kit</span>
          <span className="coming-soon-badge">Coming soon</span>
        </button>
      </div>
      <div className="nav-item">
        <NavLink
          to="/uploads"
          className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
          style={{
            justifyContent: "flex-start",
          }}
        >
          <img id="logo" src={uploadsIcon} alt="" />
          <span>Uploads</span>
        </NavLink>
      </div>
      {!IS_APP_V1 && (
        <div className="nav-item">
          <NavLink
            to="/activity"
            className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
            style={{
              justifyContent: "flex-start",
            }}
          >
            <img id="logo" src={activityIcon} alt="" />
            <span>Activity</span>
          </NavLink>
        </div>
      )}
       

      <div className="sidebar-footer">
        <div
          className={`credits-box${creditsCritical ? " credits-box--critical" : ""}${
            creditsLow && !creditsCritical ? " credits-box--low" : ""
          }${!showBuyCta ? " credits-box--compact" : ""}`}
        >
          <button
            type="button"
            className="sidebar-usage"
            onClick={openProfileUsage}
            aria-label="View credits and storage in profile"
            aria-busy={!accountDataReady}
          >
            <div className="credits-header">
              <span className="credits-header__label">
                {accountDataReady ? (
                  <>
                    {planLabel}
                    {creditsCritical ? (
                      <span className="credits-status-chip">Low</span>
                    ) : null}
                  </>
                ) : (
                  <SidebarSkeleton className="sidebar-skel--plan" />
                )}
              </span>
              <span className="credits-count">
                {accountDataReady ? (
                  balance.toLocaleString()
                ) : (
                  <SidebarSkeleton className="sidebar-skel--count" />
                )}
              </span>
            </div>

            <div className="sidebar-meter">
              <div
                className={`sidebar-meter__track${
                  !accountDataReady ? " sidebar-meter__track--loading" : ""
                }`}
                role="progressbar"
                aria-label="Credits balance"
                aria-valuemin={0}
                aria-valuemax={creditCap}
                aria-valuenow={accountDataReady ? balance : 0}
              >
                {accountDataReady ? (
                  <div
                    className={`sidebar-meter__fill sidebar-meter__fill--credits${
                      creditsCritical
                        ? " sidebar-meter__fill--critical"
                        : creditsLow
                          ? " sidebar-meter__fill--low"
                          : ""
                    }`}
                    style={{ width: `${creditsProgress}%` }}
                  />
                ) : null}
              </div>
              <div className="sidebar-meter__labels">
                <span>{creditsLabel}</span>
                <span>
                  {accountDataReady ? (
                    `${balance.toLocaleString()} / ${displayTotal.toLocaleString()}`
                  ) : (
                    <SidebarSkeleton className="sidebar-skel--value" />
                  )}
                </span>
              </div>
            </div>

            <div className="sidebar-meter">
              <div
                className={`sidebar-meter__track${
                  !accountDataReady ? " sidebar-meter__track--loading" : ""
                }`}
                role="progressbar"
                aria-label="Storage used"
                aria-valuemin={0}
                aria-valuemax={storageQuotaGb}
                aria-valuenow={accountDataReady ? storageUsedGb : 0}
              >
                {accountDataReady ? (
                  <div
                    className="sidebar-meter__fill sidebar-meter__fill--storage"
                    style={{ width: `${storageProgress}%` }}
                  />
                ) : null}
              </div>
              <div className="sidebar-meter__labels">
                <span>Storage</span>
                <span>
                  {accountDataReady ? (
                    formatStorageLabel(storageUsedGb, storageQuotaGb)
                  ) : (
                    <SidebarSkeleton className="sidebar-skel--value sidebar-skel--value-wide" />
                  )}
                </span>
              </div>
            </div>
          </button>

          {showBuyCta ? (
            <button
              type="button"
              onClick={() => setShowPricingModal(true)}
              className={`buy-credits-btn${
                creditsCritical ? " buy-credits-btn--urgent" : ""
              }`}
            >
              Buy More Credits
            </button>
          ) : null}
          {showInviteCta ? (
            <button
              type="button"
              className={`invite-btn${inviteActive ? " invite-btn--active" : ""}`}
              onClick={() => navigate("/invite")}
              aria-current={inviteActive ? "page" : undefined}
            >
              Invite & Earn
            </button>
          ) : null}

          <div
            className="user-profile-section"
            onClick={() => setShowAccountMenu(true)}
            style={{ cursor: "pointer" }}
            aria-busy={!profileReady}
          >
            {profileReady ? (
              <img
                id="user-profile-icon"
                src={user?.imageUrl || userProfileIcon}
                alt="Profile"
              />
            ) : (
              <SidebarSkeleton className="sidebar-skel--avatar" />
            )}
            <div className="profile">
              {profileReady ? (
                <span>{displayName}</span>
              ) : (
                <SidebarSkeleton className="sidebar-skel--name" />
              )}
            </div>
            <img
              id="settings-icon"
              src={settingsIcon}
              onClick={(e) => {
                e.stopPropagation();
                setShowAccountMenu(true);
              }}
              style={{ cursor: "pointer" }}
              alt="Settings"
            />
          </div>
        </div>
        {showSettingsModal && (
          <SettingsModal
            initialActiveItem={settingsActiveItem}
            lifetimeCredits={creditsBalance}
            monthlyCredits={monthlyCredits}
            storageQuotaGb={storageQuotaGb}
            storageUsedGb={storageUsedGb}
            onClose={() => setShowSettingsModal(false)}
            onChangePlan={() => {
              setShowPricingModal(true);
            }}
            onCreditsRefresh={refreshCredits}
            suppressClose={showPricingModal}
          />
        )}
        {showPricingModal && (
          <Pricing
            onClose={async () => {
              setShowPricingModal(false);
              await refreshCredits();
            }}
            onCreditsUpdated={refreshCredits}
          />
        )}
        <AccountSettingsMenu
          isOpen={showAccountMenu}
          onClose={() => setShowAccountMenu(false)}
          user={user}
          creditsBalance={creditsBalance}
          lifetimeCredits={creditsBalance}
          monthlyCredits={monthlyCredits}
          storageQuotaGb={storageQuotaGb}
          storageUsedGb={storageUsedGb}
          onOpenSettingsSection={(section) => {
            setShowAccountMenu(false);
            setSettingsActiveItem(section);
            setShowSettingsModal(true);
          }}
          onBuyMoreCredits={() => {
            setShowAccountMenu(false);
            setShowPricingModal(true);
          }}
          onLogout={handleLogout}
        />

        <div className="socials-section">
          <a
            href={SOCIAL_LINKS.x}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="LazyStudio on X"
          >
            <img id="socials-icon" src={twitterIcon} alt="" />
          </a>
          <a
            href={SOCIAL_LINKS.instagram}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="LazyStudio on Instagram"
          >
            <img id="socials-icon" src={instaIcon} alt="" />
          </a>
          <a
            href={SOCIAL_LINKS.youtube}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="LazyStudio on YouTube"
          >
            <img id="socials-icon" src={youtubeIcon} alt="" />
          </a>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
